import React from "react";
import { Button } from "react-bootstrap";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import EmptyData from "go-core/components/EmptyData";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { PartnerProvisionApi } from "../../../../../services/Api/Partner/partnerTypes";

interface PropsProvision {
	provisions: PartnerProvisionApi[];
}

interface Props {
	form: UseFormReturn<PropsProvision>;
}

const PartnerProvisionsForm = ({ form }: Props) => {
	const { t } = useTranslation("admin");
	const {
		control,
		formState: { errors },
		register,
	} = form;
	const { fields, remove, append } = useFieldArray({
		control,
		name: "provisions",
		keyName: "key",
	});
	const isMobile = useWindowSize().isMobile;

	if (fields.length === 0) {
		return (
			<EmptyData
				title={t("modules.partner.field.no_provisions.title")}
				actions={[
					{
						name: `+${t("common.action.add", { ns: "lib" })}`,
						variant: "add",
						click: () => append({} as PartnerProvisionApi),
					},
				]}
				styles={{ background: "#F5F8FA", marginBottom: "70px" }}
			/>
		);
	}

	const periods = [
		{ label: t("enums.provisions.period.YEARLY"), value: "YEARLY" },
		{ label: t("enums.provisions.period.MONTHLY"), value: "MONTHLY" },
	];

	const types = [
		{ label: t("enums.provisions.type.AMOUNT"), value: "AMOUNT" },
		{ label: t("enums.provisions.type.PERCENT"), value: "PERCENT" },
	];

	return (
		<fieldset className="form-group no-gutters">
			<h5>{t("common.word.provisions")}</h5>
			<div className={`${isMobile ? "table-responsive" : ""}`}>
				<table className={"table table-form"}>
					<thead>
						<tr>
							<th className={"action"} />
							<th style={{ minWidth: isMobile ? "100px" : "unset", width: isMobile ? "unset" : "8%" }}>
								{t("common.word.provision")}
							</th>
							<th style={{ minWidth: isMobile ? "200px" : "unset", width: isMobile ? "unset" : "15%" }}>
								{t("lib:common.word.type")}
							</th>
							<th style={{ minWidth: isMobile ? "100px" : "unset", width: isMobile ? "unset" : "5%" }}>
								{t("modules.partner.field.start_billing_period.title")}
							</th>
							<th style={{ minWidth: isMobile ? "100px" : "unset", width: isMobile ? "unset" : "8%" }}>
								{t("modules.partner.field.billing_period.title")}
							</th>
							<th style={{ minWidth: isMobile ? "100px" : "unset", width: isMobile ? "unset" : "12%" }}>
								{t("lib:common.word.source")}
							</th>
							<th style={{ minWidth: isMobile ? "200px" : "unset", width: isMobile ? "unset" : "17%" }}>
								{t("common.word.license_type")}
							</th>
							<th style={{ minWidth: isMobile ? "200px" : "150px" }}>Reference Id</th>
							<th className={"action"} />
						</tr>
					</thead>
					<tbody>
						{fields.map((provision, index) => {
							return (
								<tr key={fields[index].key}>
									<td className={"action"} />
									<td>
										<FormInput
											errors={errors}
											type={"hidden"}
											register={register}
											defaultValue={provision.id}
											name={`provisions.${index}.id`}
										/>
										<FormMoneyInput
											currency={"PLN"}
											suffix={""}
											errors={errors}
											name={`provisions.${index}.provision`}
											control={control}
										/>
									</td>
									<td>
										<FormSelectGroup
											name={`provisions.${index}.type`}
											errors={errors}
											options={types}
											control={control}
											data-testid={`provisions.${index}.type`}
										/>
									</td>
									<td>
										<FormMoneyInput
											currency={"PLN"}
											suffix={""}
											errors={errors}
											name={`provisions.${index}.start_billing_period`}
											control={control}
										/>
									</td>
									<td>
										<FormMoneyInput
											currency={"PLN"}
											suffix={""}
											errors={errors}
											name={`provisions.${index}.billing_period`}
											control={control}
										/>
									</td>
									<td>
										<FormInput
											defaultValue={provision.source}
											errors={errors}
											name={`provisions.${index}.source`}
											register={register}
										/>
									</td>
									<td>
										<FormSelectGroup
											name={`provisions.${index}.billing_period_type`}
											errors={errors}
											options={periods}
											control={control}
											data-testid={`provisions.${index}.billing_period_type`}
										/>
									</td>
									<td>
										<FormInput
											defaultValue={provision.reference_id}
											errors={errors}
											name={`provisions.${index}.reference_id`}
											register={register}
										/>
									</td>
									<td className={"action"}>
										<RemoveSVG onClick={() => remove(index)} />
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<Button variant={"add"} onClick={() => append({} as PartnerProvisionApi)}>{`+${t("common.action.add", {
				ns: "lib",
			})}`}</Button>
		</fieldset>
	);
};
export default PartnerProvisionsForm;
